import React from 'react';
import { Box, Paper, Typography, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vs } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import python from 'react-syntax-highlighter/dist/esm/languages/hljs/python';
import markdown from 'react-syntax-highlighter/dist/esm/languages/hljs/markdown';

// Регистрируем языки для подсветки синтаксиса
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('markdown', markdown);

interface CodeContextViewerProps {
  code: string[];
  highlightLineIndex: number;
  contextLines?: number;
  lineNumbers?: number[];
  cellType?: 'code' | 'markdown'; // Добавляем тип ячейки
  hideTitle?: boolean; // Добавляем опцию скрытия заголовка
}

// Используем строковый атрибут вместо булевого
const CodeLine = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isHighlighted'
})<{ isHighlighted: boolean }>(({ isHighlighted }) => ({
  padding: '2px 6px',
  fontFamily: '"Roboto Mono", monospace',
  fontSize: '12px',
  lineHeight: 1.4,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
  backgroundColor: isHighlighted ? 'rgba(14, 27, 55, 0.1)' : 'transparent',
  borderRadius: isHighlighted ? '4px' : '0',
  position: 'relative',
  '&:hover': {
    backgroundColor: isHighlighted ? 'rgba(14, 27, 55, 0.15)' : 'rgba(0, 0, 0, 0.04)',
  },
}));

const LineNumber = styled('span')(() => ({
  position: 'absolute',
  left: '-40px',
  width: '30px',
  textAlign: 'right',
  color: 'rgba(0, 0, 0, 0.4)',
  fontSize: '11px',
  userSelect: 'none',
}));

const CodeContainer = styled(Paper)(() => ({
  padding: '12px 12px 12px 48px',
  backgroundColor: '#F5F7FA',
  borderRadius: '8px',
  position: 'relative',
  overflowX: 'auto',
  marginTop: '12px',
  marginBottom: '12px',
  border: '1px solid #E5E7EB',
}));

export const CodeContextViewer: React.FC<CodeContextViewerProps> = ({
  code,
  highlightLineIndex,
  contextLines = 5,
  lineNumbers,
  cellType = 'code', // По умолчанию считаем, что это код Python
  hideTitle = false // По умолчанию показываем заголовок
}) => {
  const { startLine, endLine, codeToShow, lineNumbersToShow, isValidCode } = React.useMemo(() => {
    // Проверяем, что code - это массив
    if (!Array.isArray(code)) {
      console.error('CodeContextViewer: code должен быть массивом строк', code);
      return {
        startLine: 0,
        endLine: 0,
        codeToShow: [],
        lineNumbersToShow: [],
        isValidCode: false
      };
    }
    
    // Проверяем, что highlightLineIndex - это число
    let safeHighlightLineIndex = highlightLineIndex;
    if (typeof safeHighlightLineIndex !== 'number') {
      console.error('CodeContextViewer: highlightLineIndex должен быть числом', highlightLineIndex);
      safeHighlightLineIndex = 0;
    }
    
    const codeLength = code.length;
    
    // Корректируем highlightLineIndex, если он выходит за пределы массива
    if (safeHighlightLineIndex >= codeLength) {
      console.warn(`CodeContextViewer: highlightLineIndex (${safeHighlightLineIndex}) выходит за пределы массива (длина ${codeLength}). Устанавливаем на середину массива.`);
      safeHighlightLineIndex = Math.floor(codeLength / 2);
    }
    
    const correctedHighlightLineIndex = Math.min(Math.max(0, safeHighlightLineIndex), codeLength - 1);
    return {
      startLine: 0,
      endLine: codeLength - 1,
      codeToShow: code,
      lineNumbersToShow: lineNumbers,
      isValidCode: true
    };
  }, [code, highlightLineIndex, contextLines, lineNumbers]);
  
  // Если код невалидный, показываем сообщение об ошибке
  if (!isValidCode) {
    return (
      <Box>
        <Typography variant="subtitle1" color="error">
          Ошибка отображения контекста кода
        </Typography>
      </Box>
    );
  }
  
  // Определяем язык для подсветки синтаксиса
  const language = cellType === 'markdown' ? 'markdown' : 'python';
  
  // Создаем стиль для выделенной строки
  const highlightStyle = {
    backgroundColor: '#FFFF0044',
    display: 'block',
    width: '100%',
    borderLeft: '3px solid #CCCC00'
  };

  return (
    <Box>
      {!hideTitle && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Контекст кода {cellType === 'markdown' ? '(Markdown)' : '(Python)'}
          </Typography>
          <Divider sx={{ mb: 1 }} />
        </>
      )}
      
      <CodeContainer elevation={0}>
        {codeToShow.length > 0 ? (
          <div style={{ position: 'relative' }}>
            {/* Номера строк */}
            <div style={{ position: 'absolute', left: '-36px', top: 0 }}>
              {codeToShow.map((_, index) => {
                const actualLineNumber = lineNumbersToShow 
                  ? lineNumbersToShow[index] 
                  : index + 1;
                
                return (
                  <div 
                    key={`line-number-${index}`}
                    style={{
                      fontFamily: '"Roboto Mono", monospace',
                      fontSize: '11px',
                      color: '#888',
                      textAlign: 'right',
                      padding: '2px 6px',
                      userSelect: 'none'
                    }}
                  >
                    {actualLineNumber}
                  </div>
                );
              })}
            </div>
            
            {/* Код с подсветкой синтаксиса */}
            <div style={{ marginLeft: '8px' }}>
              {codeToShow.map((line, index) => {
                // Проверяем, является ли строка выделенной
                // Если highlightLineIndex равен 0, то выделяем первую строку (индекс 0)
                // В противном случае выделяем строку с индексом highlightLineIndex + 1
                const isHighlighted = highlightLineIndex === 0 
                  ? index === 0  // Выделяем первую строку
                  : index === highlightLineIndex + 1;  // Выделяем строку после указанной
                return (
                  <div 
                    key={`line-${index}`}
                    style={isHighlighted ? highlightStyle : undefined}
                  >
                    <SyntaxHighlighter
                      language={language}
                      style={vs}
                      customStyle={{
                        margin: 0,
                        padding: '2px 6px',
                        backgroundColor: 'transparent',
                        fontSize: '12px',
                        lineHeight: '1.4'
                      }}
                      wrapLines={true}
                    >
                      {line}
                    </SyntaxHighlighter>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px' }}>
            Нет доступного кода для отображения
          </Typography>
        )}
      </CodeContainer>
    </Box>
  );
}; 